/* ==================================================================
   CUSTOM: product image lightbox.

   Markup: snippets/custom-image-popup.liquid
   Styles: assets/component-product-image-popup.css + assets/akuna-h.css

   Kept in assets/ (not inline in the snippet) because Liquid mangles inline
   scripts - that is what silently killed the ring sizer with a SyntaxError.
   Asset files are served raw and never pass through the Liquid renderer.

   UPDATE-SAFE BY DESIGN
   Which slide to open is resolved by MEDIA ID, not by a custom index attribute:

     - stock Dawn already renders data-media-id on .product__media-toggle
       (snippets/product-thumbnail.liquid)
     - our popup slides carry data-media-id too
       (snippets/product-media-slider.liquid - our file)

   So the lightbox needs no edits to any Dawn-owned file. An earlier version
   injected a custom data-media index into product-thumbnail.liquid and
   product-media-gallery.liquid; both are Dawn-owned and were destroyed by the
   theme update. This approach cannot be broken the same way.
   ================================================================== */
(function () {
  var ZOOM_SCALE = 2.5;
  var MIN_SCALE = 1;
  var MAX_SCALE = 4;

  /* Desktop scroll-to-zoom step per wheel notch. */
  var WHEEL_STEP = 1.12;

  /* How far past the edge of a zoomed photo the finger must travel before the
     gesture is handed to the slider and it moves to the next/previous image. */
  var EDGE_THRESHOLD = 55;
  var TAP_SLOP = 8;   /* px of movement before a touch stops counting as a tap */
  var TAP_TIME = 250; /* ms */

  var isMobile = window.matchMedia('(max-width: 749px)').matches;

  var thumbSwiper = null;
  var mainSwiper = null;
  var mediaIndex = {}; /* media id -> slide index */

  /* ---------- Which media was clicked? ----------
     Reads stock Dawn's data-media-id. Falls back to the wrapping <li>, whose
     data-media-id is "sectionId-mediaId". */
  function mediaIdFromEl(el) {
    var opener = el.closest('modal-opener');
    if (opener) {
      var toggle = opener.querySelector('[data-media-id]');
      if (toggle) return toggle.getAttribute('data-media-id');
    }

    var host = el.closest('[data-media-id]');
    if (host) {
      var raw = host.getAttribute('data-media-id') || '';
      var parts = raw.split('-');
      return parts[parts.length - 1];
    }

    return null;
  }

  function resetAllZoom() {
    document.querySelectorAll('.zoom-drag-container').forEach(function (c) {
      if (c._pinchReset) c._pinchReset();
      if (c._zoomReset) c._zoomReset();
    });
  }

  /* ---------- DESKTOP: scroll to zoom + click to zoom, drag to pan ----------
     - Mouse wheel zooms in/out toward the cursor (continuous).
     - Click zooms in to ZOOM_SCALE at the clicked point; click again zooms out.
     - While zoomed, drag to pan. A click that ended a drag is ignored so a pan
       does not accidentally zoom out.
     Scrolling / clicking back to 1x snaps the image back to fit. */
  function initZoomDrag(container) {
    var img = container.querySelector('img');
    if (!img) return;

    img.style.transformOrigin = '0 0';

    var scale = 1;
    var px = 0;
    var py = 0;
    var dragging = false;
    var moved = false;
    var startX = 0;
    var startY = 0;
    var startPx = 0;
    var startPy = 0;

    function size() {
      var r = container.getBoundingClientRect();
      return { w: r.width, h: r.height };
    }

    function clampPan() {
      var s = size();
      var minX = s.w * (1 - scale);
      var minY = s.h * (1 - scale);
      px = Math.min(0, Math.max(minX, px));
      py = Math.min(0, Math.max(minY, py));
    }

    function apply(animated) {
      img.style.transition = animated ? 'transform 0.15s ease' : 'none';
      img.style.transform = 'translate(' + px + 'px, ' + py + 'px) scale(' + scale + ')';
      var zoomed = scale > 1.001;
      container.dataset.zoomed = zoomed ? 'true' : 'false';
      container.classList.toggle('is-zoomed', zoomed);
    }

    container._zoomReset = function () {
      scale = 1;
      px = 0;
      py = 0;
      dragging = false;
      container.classList.remove('is-dragging');
      apply(true);
    };

    /* Zoom to a target scale, keeping the container point (ax, ay) under it fixed. */
    function zoomTo(target, ax, ay, animated) {
      var prev = scale;
      scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, target));
      if (scale <= 1) {
        scale = 1;
        px = 0;
        py = 0;
        apply(animated);
        return;
      }
      var lx = (ax - px) / prev;
      var ly = (ay - py) / prev;
      px = ax - lx * scale;
      py = ay - ly * scale;
      clampPan();
      apply(animated);
    }

    /* SCROLL TO ZOOM toward the cursor. */
    container.addEventListener('wheel', function (e) {
      e.preventDefault();
      var r = container.getBoundingClientRect();
      var factor = e.deltaY < 0 ? WHEEL_STEP : 1 / WHEEL_STEP;
      zoomTo(scale * factor, e.clientX - r.left, e.clientY - r.top, false);
    }, { passive: false });

    /* DRAG TO PAN while zoomed. */
    container.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      moved = false;
      if (scale <= 1) return;
      e.preventDefault();
      container.setPointerCapture(e.pointerId);
      dragging = true;
      startX = e.clientX;
      startY = e.clientY;
      startPx = px;
      startPy = py;
      container.classList.add('is-dragging');
    });

    container.addEventListener('pointermove', function (e) {
      if (!dragging || scale <= 1) return;
      e.preventDefault();
      if (Math.abs(e.clientX - startX) > 3 || Math.abs(e.clientY - startY) > 3) moved = true;
      px = startPx + (e.clientX - startX);
      py = startPy + (e.clientY - startY);
      clampPan();
      apply(false);
    });

    function endDrag() {
      if (!dragging) return;
      dragging = false;
      container.classList.remove('is-dragging');
    }
    container.addEventListener('pointerup', endDrag);
    container.addEventListener('pointercancel', endDrag);

    /* CLICK TO ZOOM. A click that ended a pan (image was dragged) is ignored. */
    container.addEventListener('click', function (e) {
      if (moved) {
        moved = false;
        return;
      }
      var r = container.getBoundingClientRect();
      if (scale > 1) {
        zoomTo(1, 0, 0, true);
      } else {
        zoomTo(ZOOM_SCALE, e.clientX - r.left, e.clientY - r.top, true);
      }
    });
  }

  /* ---------- MOBILE: pinch, pan, tap-to-zoom-out, swipe-at-edge ---------- */
  function initPinchZoom(container) {
    var img = container.querySelector('img');
    if (!img) return;

    var scale = 1;
    var tx = 0;
    var ty = 0;

    var startDist = 0;
    var startScale = 1;
    var startTx = 0;
    var startTy = 0;

    var pinching = false;
    var panning = false;
    var panX = 0;
    var panY = 0;
    var panTx = 0;
    var panTy = 0;

    var tapTime = 0;
    var tapX = 0;
    var tapY = 0;
    var tapMoved = false;
    var edgeHandled = false;

    function dist(t) {
      var dx = t[0].clientX - t[1].clientX;
      var dy = t[0].clientY - t[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    }

    function bounds(s) {
      var r = container.getBoundingClientRect();
      return {
        minX: -(r.width * s - r.width) / s,
        minY: -(r.height * s - r.height) / s
      };
    }

    function clamp(x, y, s) {
      var b = bounds(s);
      return { x: Math.min(0, Math.max(b.minX, x)), y: Math.min(0, Math.max(b.minY, y)) };
    }

    function apply(animated) {
      img.style.transition = animated ? 'transform 0.25s ease' : 'none';
      img.style.transform = 'scale(' + scale + ') translate(' + tx + 'px, ' + ty + 'px)';
      container.dataset.zoomed = scale > 1 ? 'true' : 'false';
      container.classList.toggle('is-zoomed', scale > 1);
    }

    function reset(animated) {
      scale = 1;
      tx = 0;
      ty = 0;
      apply(animated !== false);
    }

    container._pinchReset = function () {
      reset(true);
    };

    container.addEventListener('touchstart', function (e) {
      edgeHandled = false;

      if (e.touches.length === 1) {
        tapTime = Date.now();
        tapX = e.touches[0].clientX;
        tapY = e.touches[0].clientY;
        tapMoved = false;
      }

      if (e.touches.length === 2) {
        pinching = true;
        panning = false;
        startDist = dist(e.touches);
        startScale = scale;
        startTx = tx;
        startTy = ty;
      } else if (e.touches.length === 1 && scale > 1) {
        panning = true;
        panX = e.touches[0].clientX;
        panY = e.touches[0].clientY;
        panTx = tx;
        panTy = ty;
      }
    }, { passive: true });

    container.addEventListener('touchmove', function (e) {
      if (e.touches.length === 1) {
        if (
          Math.abs(e.touches[0].clientX - tapX) > TAP_SLOP ||
          Math.abs(e.touches[0].clientY - tapY) > TAP_SLOP
        ) {
          tapMoved = true;
        }
      }

      if (pinching && e.touches.length === 2) {
        e.preventDefault();
        e.stopPropagation();
        scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, startScale * (dist(e.touches) / startDist)));
        var c = clamp(startTx, startTy, scale);
        tx = c.x;
        ty = c.y;
        apply(false);
        return;
      }

      if (panning && e.touches.length === 1 && scale > 1) {
        if (edgeHandled) return;

        var dx = (e.touches[0].clientX - panX) / scale;
        var dy = (e.touches[0].clientY - panY) / scale;
        var rawX = panTx + dx;
        var b = bounds(scale);

        var pastLeft = (rawX - 0) * scale;
        var pastRight = (b.minX - rawX) * scale;

        /* SWIPE WHILE ZOOMED. The photo is already hard against its edge and the
           finger is still going, so hand the gesture to the slider instead of
           stubbornly doing nothing. */
        if (mainSwiper) {
          if (pastLeft > EDGE_THRESHOLD) {
            edgeHandled = true;
            panning = false;
            reset(false);
            mainSwiper.slidePrev();
            return;
          }
          if (pastRight > EDGE_THRESHOLD) {
            edgeHandled = true;
            panning = false;
            reset(false);
            mainSwiper.slideNext();
            return;
          }
        }

        e.preventDefault();
        e.stopPropagation();

        var cp = clamp(rawX, panTy + dy, scale);
        tx = cp.x;
        ty = cp.y;
        apply(false);
      }
    }, { passive: false });

    container.addEventListener('touchend', function (e) {
      if (e.touches.length === 0) {
        /* TAP TO ZOOM OUT: a quick, still tap while zoomed springs back to fit. */
        if (!tapMoved && !pinching && !edgeHandled && scale > 1 && Date.now() - tapTime < TAP_TIME) {
          reset(true);
        }
        pinching = false;
        panning = false;
        edgeHandled = false;
        if (scale <= 1.05 && scale !== 1) reset(true);
      } else if (e.touches.length === 1) {
        /* Lifted one finger out of a pinch: continue as a pan. */
        pinching = false;
        panning = scale > 1;
        panX = e.touches[0].clientX;
        panY = e.touches[0].clientY;
        panTx = tx;
        panTy = ty;
        tapMoved = true;
      }
    }, { passive: true });

    container.addEventListener('touchcancel', function () {
      pinching = false;
      panning = false;
      edgeHandled = false;
    }, { passive: true });
  }

  function initZoomContainers() {
    document.querySelectorAll('.zoom-drag-container:not([data-zoom-init])').forEach(function (c) {
      c.setAttribute('data-zoom-init', 'true');
      if (isMobile) {
        initPinchZoom(c);
      } else {
        initZoomDrag(c);
      }
    });
  }

  function init() {
    if (typeof Swiper === 'undefined') {
      console.warn('[custom-image-popup] Swiper is not defined - the lightbox will not work.');
      return;
    }

    var mainEl = document.querySelector('.product-image-popup-slider--main');
    var thumbsEl = document.querySelector('.product-image-popup-thumbnail-slider--main');
    var popup = document.querySelector('.product-image-popup-container-sticky');
    if (!mainEl || !thumbsEl || !popup || mainEl.dataset.trcInit) return;

    mainEl.dataset.trcInit = 'true';

    /* Map media id -> slide index BEFORE Swiper runs, because loop mode clones
       slides and would corrupt the indices. */
    var slides = mainEl.querySelectorAll('.swiper-wrapper > .swiper-slide');
    for (var i = 0; i < slides.length; i++) {
      var id = slides[i].getAttribute('data-media-id');
      if (id) mediaIndex[id] = i;
    }

    thumbSwiper = new Swiper(thumbsEl, {
      loop: true,
      spaceBetween: 10,
      freeMode: true,
      watchSlidesProgress: true,
      breakpoints: {
        0: { slidesPerView: 5 },
        768: { slidesPerView: 7 },
        1024: { slidesPerView: 10 }
      }
    });

    mainSwiper = new Swiper(mainEl, {
      loop: true,
      spaceBetween: 10,
      navigation: {
        nextEl: '.product-image-button-next',
        prevEl: '.product-image-button-prev'
      },
      thumbs: { swiper: thumbSwiper }
    });

    mainSwiper.on('slideChange', resetAllZoom);

    function close() {
      popup.classList.remove('active');
      document.body.style.overflow = '';
      resetAllZoom();
    }

    document.querySelectorAll('.product-image-popup-close').forEach(function (btn) {
      btn.addEventListener('click', close);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && popup.classList.contains('active')) close();
    });

    /* Delegated, so it survives Dawn re-rendering the gallery on a variant change. */
    document.addEventListener('click', function (e) {
      var media = e.target.closest('.product__media');
      if (!media) return;
      if (media.closest('.product-image-popup-container-sticky')) return;

      var id = mediaIdFromEl(media);
      var index = id != null && mediaIndex[id] != null ? mediaIndex[id] : 0;

      popup.classList.add('active');
      document.body.style.overflow = 'hidden';

      setTimeout(function () {
        thumbSwiper.update();
        mainSwiper.update();
        mainSwiper.slideToLoop(index, 0);
        initZoomContainers();
      }, 100);
    });

    initZoomContainers();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('load', init);
  document.addEventListener('shopify:section:load', init);

  new MutationObserver(initZoomContainers).observe(document.body, { childList: true, subtree: true });

  document.addEventListener('click', function (e) {
    if (e.target.closest('.product-image-button-next, .product-image-button-prev')) resetAllZoom();
    if (e.target.closest('[thumbsSlider] .swiper-slide')) resetAllZoom();
  }, true);
})();
